import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import CLMHome from "./components/CLMHome";
import AddDocumentModal from "./components/modals/AddDocumentModal";
import ChooseTemplate from "./components/ChooseTemplate";
import LoginPage from "./components/LoginPage";
import RegisterPage from "./components/RegisterPage";
import ExternalLoginPage from "./components/ExternalLoginPage";
import SalesContracts from "./components/SalesContracts";
import PurchaseOrders from "./components/PurchaseOrders";
// import ContractsTable from "./components/table/ContractsTable";
import TrashPage from "./components/table/TrashPage";
import RecentsPage from "./components/table/RecentsPage";
// import DummyTreeTable from "./components/TreeTable";
import CategoriesPage from "./components/CategoriesSection";
import Templates from "./components/Templates";
import ProtectedRoute from "./components/rbac/ProtectedRoute";
import { RBACProvider } from "./components/rbac/RbacProvider";
import FavouritesPage from "./components/FavouritesPage";
import Roles from "./components/Role";
import ContractStatus from "./components/ContractStatus";
import ContractTypes from './components/ContractTypes';
import Practices from './components/Practices';
import Departments from './components/Departments';
import Currency from './components/Currency';
import WIPContracts from './components/WipContracts';
import AdminDashboard from './components/Dashboard/Dashboard';
import Clients from './components/Client';
import IncompleteContracts from './components/IncompleteContracts';
import "./App.css";
import UnauthorizedPage from "./components/rbac/UnauthorizedPage";
import FetchUserDetails from "./components/UserDetails/FetchUserDetails";
 
 
// ============ HELPER FUNCTIONS FOR AUTH STATUS ============

// Helper: Extract and store tokens from URL
const extractAndStoreTokens = (urlParams) => {
  const accessToken = urlParams.get('access_token');
  const refreshToken = urlParams.get('refresh_token');
  
  if (accessToken && refreshToken) {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
    console.log('[LOG] JWT tokens stored from URL parameters');
  }
  
  return accessToken;
};

// Helper: Get role from query parameters
const getRoleFromQuery = (urlParams) => {
  const roleFromQuery = urlParams.get("user_role");
  const roleIdFromQuery = urlParams.get("role_id");
  
  if (!roleFromQuery && !roleIdFromQuery) {
    return null;
  }
  
  const roleMap = {
    "1": "Admin",
    "2": "LegalReviewer",
    "3": "LegalUser",
    "4": "Finance",
    "5": "ProjectManagementOffice",
    "6": "TalentAcquisitionGroup",
    "7": "ClientPartners",
  };
  
  return (roleFromQuery || roleMap[roleIdFromQuery] || "LegalUser").replace(/\s+/g, '');
};

// Helper: Handle role from query params
const handleRoleFromQuery = (role, setUserRole, setIsCheckingAuth, setIsLoading) => {
  console.log('[LOG] Setting role from query params:', role);
  setUserRole(role);
  sessionStorage.setItem("user_role", role);
  window.history.replaceState({}, document.title, window.location.pathname);
  setIsCheckingAuth(false);
  if (setIsLoading) setIsLoading(false);
};

// Helper: Get stored role from sessionStorage
const getStoredRole = () => {
  return (sessionStorage.getItem("user_role") || "").replace(/\s+/g, '');
};

// Helper: Handle stored role found
const handleStoredRole = (storedRole, setUserRole, setIsCheckingAuth, setIsLoading) => {
  console.log('[LOG] Role from sessionStorage:', storedRole);
  setUserRole(storedRole);
  setIsCheckingAuth(false);
  if (setIsLoading) setIsLoading(false);
};

// Helper: Fetch and set user role from API
const fetchAndSetUserRole = async (setUserRole, setIsCheckingAuth, setIsLoading) => {
  console.log('[LOG] Token found but no role in sessionStorage, fetching user details...');
  try {
    const userFetched = await FetchUserDetails();
    if (userFetched) {
      console.log('[LOG] User details fetched successfully in AuthInitializer');
      const newRole = getStoredRole();
      if (newRole) {
        console.log('[LOG] Setting role from fetched user details:', newRole);
        setUserRole(newRole);
      }
    } else {
      console.error('[LOG] Failed to fetch user details in AuthInitializer');
    }
  } catch (fetchError) {
    console.error('[LOG] Error fetching user details in AuthInitializer:', fetchError);
  } finally {
    setIsCheckingAuth(false);
    if (setIsLoading) setIsLoading(false);
  }
};

// Helper: Handle no token and no role scenario
const handleNoTokenAndRole = (setIsCheckingAuth, setIsLoading) => {
  setIsCheckingAuth(false);
  if (setIsLoading) setIsLoading(false);
};

// Wrapper component to handle post-login query params or fetch user details if needed
function AuthInitializer({ children, setUserRole, setIsLoading }) {
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = React.useState(true);
 
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Set loading to true initially (both internal and external)
        setIsCheckingAuth(true);
        if (setIsLoading) setIsLoading(true);
       
        // Check for JWT tokens in URL parameters (from SSO redirect)
        const urlParams = new URLSearchParams(location.search);
        const accessToken = extractAndStoreTokens(urlParams);
        
        // Check if we have an access token (from URL or localStorage)
        const token = accessToken || localStorage.getItem('access_token');
       
        // Handle role from query params first
        const roleFromQuery = getRoleFromQuery(urlParams);
        
        if (roleFromQuery) {
          handleRoleFromQuery(roleFromQuery, setUserRole, setIsCheckingAuth, setIsLoading);
        } else {
          // Check sessionStorage for role
          const storedRole = getStoredRole();
          
          if (storedRole) {
            handleStoredRole(storedRole, setUserRole, setIsCheckingAuth, setIsLoading);
          } else if (token) {
            // Fetch user details if we have token but no role
            await fetchAndSetUserRole(setUserRole, setIsCheckingAuth, setIsLoading);
          } else {
            // No token and no role
            handleNoTokenAndRole(setIsCheckingAuth, setIsLoading);
          }
        }
      } catch (error) {
        console.error('Error processing auth status:', error);
        setIsCheckingAuth(false);
        if (setIsLoading) setIsLoading(false);
      }
    };
 
    checkAuthStatus();
   
    // Also listen for storage events to sync role changes
    const handleStorageChange = (e) => {
      if (e.key === 'user_role') {
        const updatedRole = (e.newValue || "").replace(/\s+/g, '');
        console.log('[LOG] Storage event - role updated:', updatedRole);
        if (updatedRole) {
          setUserRole(updatedRole);
        }
      }
    };
   
    window.addEventListener('storage', handleStorageChange);
   
    // Also check sessionStorage periodically in case of same-tab updates
    const intervalId = setInterval(() => {
      const storedRole = (sessionStorage.getItem("user_role") || "").replace(/\s+/g, '');
      if (storedRole && storedRole !== location.search) {
        setUserRole(storedRole);
      }
    }, 500);
   
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [location.search, setUserRole, setIsLoading]);
 
  // Don't render children until auth check is complete
  if (isCheckingAuth) {
    return <div>Loading...</div>;
  }
 
  return children;
}
 function App() {
   const [userRole, setUserRole] = useState(() => {
  const storedRole = (sessionStorage.getItem("user_role") || "").replace(/\s+/g, '');
  console.log('[LOG] Initial userRole from sessionStorage:', storedRole);
  return storedRole || null;
});
 
  const [isLoadingAuth, setIsLoadingAuth] = useState(() => {
    // Check if we have a token but no role - means we need to fetch
    const token = localStorage.getItem('access_token');
    const role = sessionStorage.getItem("user_role");
    // Only show loading if we have a token but no role
    return !!(token && !role);
  });
 
useEffect(() => {
  // Check sessionStorage on mount and when it changes
  const checkRole = () => {
    const storedRole = (sessionStorage.getItem("user_role") || "").replace(/\s+/g, '');
    console.log('[LOG] Checking role in sessionStorage:', storedRole, 'Current state:', userRole);
    if (storedRole && storedRole !== userRole) {
      console.log('[LOG] Updating userRole state from', userRole, 'to', storedRole);
      setUserRole(storedRole);
      setIsLoadingAuth(false);
    }
  };
 
  // Check immediately
  checkRole();
 
  // Listen for storage events (cross-tab)
  const handleStorageChange = (e) => {
    if (e.key === 'user_role') {
      const updatedRole = (e.newValue || "").replace(/\s+/g, '');
      console.log('[LOG] Storage event received, updating role:', updatedRole);
      if (updatedRole) {
        setUserRole(updatedRole);
        setIsLoadingAuth(false);
      }
    }
  };
 
  // Listen for custom event (same-tab updates)
  const handleUserRoleUpdated = (e) => {
    const updatedRole = (e.detail?.role || "").replace(/\s+/g, '');
    console.log('[LOG] Custom userRoleUpdated event received, updating role:', updatedRole);
    if (updatedRole) {
      setUserRole(updatedRole);
      setIsLoadingAuth(false);
    }
  };
 
  window.addEventListener("storage", handleStorageChange);
  window.addEventListener("userRoleUpdated", handleUserRoleUpdated);
 
  // Also check periodically for same-tab updates (since storage event doesn't fire in same tab)
  const intervalId = setInterval(checkRole, 1000);
 
  return () => {
    window.removeEventListener("storage", handleStorageChange);
    window.removeEventListener("userRoleUpdated", handleUserRoleUpdated);
    clearInterval(intervalId);
  };
}, [userRole]);
 
 
  const handleLogin = (role) => {
  console.log("[LOG] handleLogin called with role:", role);
  const normalizedRole = (role || "").replace(/\s+/g, '');
  console.log("[LOG] Normalized role:", normalizedRole);
  if (normalizedRole) {
    setUserRole(normalizedRole);
    sessionStorage.setItem("user_role", normalizedRole); // Store normalized role
    console.log("[LOG] Role stored in sessionStorage:", normalizedRole);
  }
};
 
const routes = [
  { path: "/clm_home", element: <CLMHome />, allowedKey: "AllContracts" },
  { path: "/choose-template", element: <ChooseTemplate />, allowedKey: "Templates" },
  { path: "/sales-contracts", element: <SalesContracts />, allowedKey: "AllContracts" },
  { path: "/purchase-orders", element: <PurchaseOrders />, allowedKey: "AllContracts" },
  // { path: "/contracts", element: <ContractsTable />, allowedKey: "AllContracts" },
  { path: "/wip-contracts", element: <WIPContracts />, allowedKey: "WIPContracts" },
  { path: "/trash", element: <TrashPage />, allowedKey: "Trash" },
  { path: "/recents", element: <RecentsPage />, allowedKey: "Recent" },
  { path: "/favourites", element: <FavouritesPage />, allowedKey: "Favourite" },
  {
    path: "/add-document",
    element: <AddDocumentModal isOpen={true} onClose={() => {}} onCreateBlank={() => {}} />,
    allowedKey: "AllContracts"
  },
  // { path: "/dummy-treetable", element: <DummyTreeTable />, allowedKey: "AllContracts" },
  { path: "/categories", element: <CategoriesPage />, allowedKey: "Categories" },
  { path: "/category/:id", element: <div>Category Detail Page</div>, allowedKey: "Categories" },
  { path: "/templates", element: <Templates />, allowedKey: "Templates" },
  { path: "/roles", element: <Roles />, allowedKey: "Roles" },
  { path: "/contract-status", element: <ContractStatus />, allowedKey: "ContractStatus" },
  { path: "/contract-types", element: <ContractTypes />, allowedKey: "ContractTypes" },
  { path: "/practices", element: <Practices />, allowedKey: "Practices" },
  { path: "/departments", element: <Departments />, allowedKey: "Departments" },
  { path: "/currencies", element: <Currency />, allowedKey: "Currency" },
  { path: "/currency", element: <Currency />, allowedKey: "Currency" },
  { path: "/admin-dashboard", element: <AdminDashboard />, allowedKey: "AdminDashboard" },
  { path: "/clients", element: <Clients />, allowedKey: "Clients" },
  { path: "/incomplete-contracts", element: <IncompleteContracts />, allowedKey: "IncompleteContracts" },
 
];
  return (
    <RBACProvider role={userRole}>
     
      <Router>
        <div className="App">
        <Routes>
  {/* Login routes */}
  <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
  <Route path="/register" element={<RegisterPage />} />
  <Route path="/external-login" element={<ExternalLoginPage />} />
  <Route path="/unauthorized" element={<UnauthorizedPage />} />
 
  {/* Root route: redirect to login or home based on auth */}
  <Route
    path="/"
    element={
      userRole && userRole.trim() !== "" ? (
        <Navigate to="/clm_home" replace />
      ) : (
        <Navigate to="/login" replace />
      )
    }
  />
 
  {/* Dynamic routes */}
  {routes.map(({ path, element, allowedKey }) => (
    <Route
      key={path}
      path={path}
      element={
        <AuthInitializer setUserRole={setUserRole} setIsLoading={setIsLoadingAuth}>
          {userRole && userRole.trim() !== "" ? (
            <ProtectedRoute element={element} allowedKey={allowedKey} />
          ) : (
            <Navigate to="/unauthorized" replace />
          )}
        </AuthInitializer>
      }
    />
  ))}
</Routes>
        </div>
      </Router>
     
    </RBACProvider>
  );
}
 
export default App;